import { React, useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogTitle, Snackbar } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import DownloadIcon from "@mui/icons-material/Download";
import Tooltip from "@mui/material/Tooltip";
import PropTypes from "prop-types";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "./vfs_fonts";

function CompanyBillingEmail({
  company,
  updateTable,
  setUpdateTable,
  setOpenSnackbar,
  setSnackbarMessage,
}) {
  const [billingDate, setBillingDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState();
  const [invoiceReminder, setInvoiceReminder] = useState(false);

  const receiveAddress = company.primaryContact.email;
  const subject = `Reminder: Upcoming Payment for ${company.membership.membershipType} Partnership`;

  const invoicedReminder = () => {
    /** Only need the invoice reminder once */
    if(!invoiceReminder) {
      alert("Don't forget to 'Save Billed Date' to mark the Employer as having been invoiced.");
      setInvoiceReminder(true);
    }
  };

  useEffect(() => {
    /** Reset the invoice reminder everytime the dialog closes */
    if (!openDialog) {
      setInvoiceReminder(false);
    }
  }, [openDialog]);

  const handleSendEmail = () => {
    invoicedReminder();
    const mailTo = `mailto:${encodeURIComponent(
      receiveAddress
    )}?subject=${encodeURIComponent(subject)}`;
    window.location.href = mailTo;
  };

  const handleCaptureBillingDate = (event) => {
    event.preventDefault();

    if (isNaN(billingDate.getTime())) {
      setSnackbarMessage("Invalid billing date. Please enter a valid date.");
      setOpenSnackbar(true);
      return;
    }

    const formatDate = billingDate.toISOString().split("T")[0];

    const formData = new FormData();
    formData.append("employerId", company.employerId);
    formData.append("billingDate", formatDate);
    formData.append("paymentDate", "");

    axios.post("/company/editCompanyBilling", formData).then((res) => {
      if (res.data.success) {
        setSnackbarMessage(`Billing date captured as ${formatDate}`);
        setOpenSnackbar(true);
        setUpdateTable(!updateTable);
        setOpenDialog(false);
      } else {
        setSnackbarMessage(
          `Failed to capture billing date. ${res.data.message}`
        );
        setOpenSnackbar(true);
      }
    });
  };

  // const handleOpenEmailDialog = () => {
  //   //Temp fix, generate invoice num when billing date is captured.
  //   const formData = new FormData();
  //   formData.append(
  //     "companyMembershipType",
  //     company.membership.membershipType[0]
  //   );

  //   axios.post("/company/createBillingInvoiceNumber", formData).then((res) => {
  //     if (res.data.success) {
  //       setInvoiceNumber(res.data.invoiceNumber);
  //     } else {
  //       setSnackbarMessage("Failed to generate invoice number.");
  //       setOpenSnackbar(true);
  //     }
  //   });
  //   setOpenDialog(true);
  // };

  const generateBillingPdf = async () => {
    invoicedReminder();
    const formData = new FormData();
    let membershipAbbreviation = company.membership.membershipType[0]; // use slice to be safe
    if(membershipAbbreviation === "C")
      membershipAbbreviation = "CF";

    formData.append(
      "companyMembershipType",
      membershipAbbreviation
    );

    try {
      const res = await axios.post(
        "/company/createBillingInvoiceNumber",
        formData
      );

      if (!res.data.success) {
        setSnackbarMessage("Failed to generate invoice number.");
        setOpenSnackbar(true);
        return;
      }

      const invoiceNumber = res.data.invoiceNumber;
      setInvoiceNumber(invoiceNumber);

      const billFormatDate = billingDate.toISOString().split("T")[0];
      const billYear = billFormatDate.split("-")[0] % 100;
      const billMonth = billFormatDate.split("-")[1];
      const billDay = billFormatDate.split("-")[2];
      const remitPayDate = new Date(billingDate);
      remitPayDate.setDate(remitPayDate.getDate() + 30);
      const remitPayFormatDate = remitPayDate.toISOString().split("T")[0];
      const remitPayYear = remitPayFormatDate.split("-")[0] % 100;
      const remitPayMonth = remitPayFormatDate.split("-")[1];
      const remitPayDay = remitPayFormatDate.split("-")[2];
      const startParts = company.startDate.split("-");
      const endParts = company.endDate.split("-");
      const startDate = new Date(
        Number(startParts[0]),
        Number(startParts[1]) - 1,
        Number(startParts[2])
      ).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const endDate = new Date(
        Number(endParts[0]),
        Number(endParts[1]) - 1,
        Number(endParts[2])
      ).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const billingAddress = company.billingAddress;
      const billingSplit = billingAddress.split(", ");
      var addressFirst = " ";
      var addressSecond = " ";
      if (billingSplit.length > 2){
        addressFirst = billingSplit.slice(0, Math.ceil(billingSplit.length / 2)).join(", ");
        addressSecond = billingSplit.slice(Math.ceil(billingSplit.length / 2)).join(", ");
      }
      else{
        addressFirst = company.billingAddress;
      }


      pdfMake.vfs = pdfFonts;
      pdfMake.fonts = {
        OpenSans: {
          normal: "OpenSans-Regular.ttf",
          bold: "OpenSans-Bold.ttf",
          italics: "OpenSans-Italic.ttf",
          bolditalics: "OpenSans-BoldItalic.ttf",
        },
      };
      const docDefinition = {
        pageSize: "LETTER",

        info: {
          title: `${company.name.toUpperCase()}-${
            company.membership.membershipType
          }-Invoice-${new Date().getFullYear()}`,
        },

        defaultStyle: {
          font: "OpenSans",
        },

        content: [
          {
            image: "emailTitleImage.jpg",
            width: 281,
            margin: [140, 0, 0, 0],
          },
          {
            text: "ISSUED TO:",
            style: "boldHeader100",
            margin: [60, 30, 0, 0],
          },
          {
            columns: [
              {
                text: `${company.primaryContact.name}`, //Need to know what type of contact they want.
                fontSize: 10,
                italics: true,
                margin: [60, 3, 0, 0],
                alignment: "left",
              },
              {
                columns: [
                  {
                    text: "INVOICE DATE: ",
                    style: "boldHeader105",
                    margin: [0, 0, 0, 0],
                    alignment: "right",
                  },
                  {
                    text: `${billMonth}.${billDay}.${billYear}`,
                    fontSize: 10.5,
                    margin: [0, 0, 60, 0],
                    alignment: "right",
                  },
                ],
              },
            ],
          },
          {
            text: `${company.name}`,
            style: "boldHeader100",
            margin: [60, 0, 0, 0],
          },
          {
            columns: [
              {
                text: `${addressFirst}`,
                fontSize: 10,
                margin: [62, 0, 0, 0],
                alignment: "left",
              },
              {
                columns: [
                  {
                    text: "INVOICE NO: ",
                    style: "boldHeader105",
                    margin: [0, 0, 0, 0],
                    alignment: "right",
                  },
                  {
                    text: `#${invoiceNumber}`,
                    fontSize: 10.5,
                    margin: [0, 0, 60, 0],
                    alignment: "right",
                  },
                ],
              },
            ],
          },
          {
            text: `${addressSecond}`,
            fontSize: 10,
            margin: [60, 0, 0, 0],
            alignment: "left",
          },
          {
            columns: [
              {
                text: `${company.primaryContact.email}`, //Need detailed address
                fontSize: 10,
                margin: [60, 0, 0, 0],
                alignment: "left",
              },
              {
                columns: [
                  {
                    text: "REMIT PAYMENT BY:",
                    style: "boldHeader105",
                    margin: [0, 0, 0, 0],
                    alignment: "right",
                  },
                  {
                    text: `${remitPayMonth}.${remitPayDay}.${remitPayYear}`,
                    fontSize: 10.5,
                    margin: [0, 0, 60, 0],
                    alignment: "right",
                  },
                ],
              },
            ],
          },
          {
            canvas: [
              {
                type: "line",
                x1: 58,
                y1: 5,
                x2: 473,
                y2: 5,
                lineWidth: 1,
              },
            ],
          },
          {
            columns: [
              {
                text: "DESCRIPTION",
                style: "boldHeader110",
                margin: [60, 10, 0, 10],
                alignment: "left",
              },
              {
                text: "TOTAL",
                style: "boldHeader110",
                margin: [0, 10, 60, 10],
                alignment: "right",
              },
            ],
          },
          {
            canvas: [
              {
                type: "line",
                x1: 58,
                y1: 0,
                x2: 473,
                y2: 0,
                lineWidth: 1,
              },
            ],
          },
          {
            columns: [
              {
                text: `${company.membership.membershipType} Level Benefits`,
                style: "boldHeader110",
                margin: [56, 10, 0, 0],
                alignment: "left",
              },
              {
                text: `${company.membership.cost.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}`,
                fontSize: 11,
                margin: [0, 10, 60, 0],
                alignment: "right",
              },
            ],
          },
          {
            text: `${startDate} - ${endDate}`,
            fontSize: 11,
            italics: true,
            margin: [56, 0, 0, 0],
          },
          {
            text: "CS|Source Partnership Program",
            fontSize: 11,
            margin: [56, 0, 0, 0],
          },
          {
            text: "Department of Computer Science, Virginia Tech",
            fontSize: 11,
            margin: [56, 0, 0, 0],
          },
          {
            canvas: [
              {
                type: "line",
                x1: 55,
                y1: 15,
                x2: 473,
                y2: 15,
                lineWidth: 1,
              },
            ],
          },
          {
            columns: [
              {
                text: "TOTAL",
                style: "boldHeader110",
                margin: [60, 10, 0, 10],
                alignment: "left",
              },
              {
                text: `${company.membership.cost.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}`,
                style: "boldHeader110",
                margin: [0, 10, 60, 10],
                alignment: "right",
              },
            ],
          },
          {
            canvas: [
              {
                type: "line",
                x1: 55,
                y1: 0,
                x2: 473,
                y2: 0,
                lineWidth: 1,
              },
            ],
          },
          {
            text: "PAYMENT OPTIONS",
            style: "boldHeader110",
            margin: [60, 15, 0, 10],
          },
          {
            text: "Check",
            style: "boldHeader110",
            margin: [60, 5, 0, 0],
          },
          {
            stack: [
              {
                image: "thankYou.png",
                width: 118,
                absolutePosition: { x: 410, y: 508 },
              },
              {
                text: "Payable to:",
              },
              {
                text: "Treasurer, Virginia Tech Foundation",
                italics: true,
              },
              {
                text: "Mail to:",
                margin: [0, 10, 0, 0],
              },
              {
                text: "Department of Computer Science",
                italics: true,
              },
              {
                text: "Attn: CS|Source",
                italics: true,
              },
              {
                text: "Virginia Tech",
                italics: true,
              },
              {
                text: "620 Drillfield Drive",
                italics: true,
              },
              {
                text: "3160 Torgerson Hall",
                italics: true,
              },
              {
                text: "Blacksburg, VA 24061",
                italics: true,
              },
            ],
            margin: [73, 0, 0, 0],
            fontSize: 11,
          },
          {
            text: "For credit card, EFT, or internal fund transfer, please contact",
            style: "boldHeader110",
            margin: [60, 10, 60, 0],
          },
          {
            text: "Makayla Welch at mhwelch9821@vt.edu / 540-231-1666",
            style: "boldHeader110",
            margin: [60, 0, 60, 0],
          },
        ],
        styles: {
          boldHeader100: {
            font: "OpenSans",
            fontSize: 10,
            bold: true,
          },
          boldHeader105: {
            font: "OpenSans",
            fontSize: 10.5,
            bold: true,
          },
          boldHeader110: {
            font: "OpenSans",
            fontSize: 11,
            bold: true,
          },
        },
      };

      pdfMake
        .createPdf(docDefinition)
        .download(
          `${company.name.toUpperCase()}-${
            company.membership.membershipType
          }-Invoice-${new Date().getFullYear()}.pdf`
        );
    } catch (err) {
      console.error(err);
      setSnackbarMessage("Failed: An unexpected error occurred.");
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      <Tooltip title="Send a billing email to the company">
        <button onClick={() => setOpenDialog(true)}>
          <EmailIcon fontSize="large" className="fill-brand-quaternary" />
        </button>
      </Tooltip>

      <Dialog open={openDialog} fullWidth maxWidth="md">
        <DialogTitle className="mx-auto text-2xl font-bold font-brand-primary">
          Email Preview
        </DialogTitle>
        <div className="flex flex-col mb-4 mx-8">
          <div className="text-lg font-brand-primary">
            <p>
              <strong>Billing Email for {company.name}</strong>
            </p>
            <p>
              <strong>Partnership: </strong>
              {company.membership.membershipType}
            </p>
            <p>
              <strong>To: </strong>
              {receiveAddress}
            </p>
            <p>
              <strong>Subject: </strong>
              {subject}
            </p>
          </div>

          <div className="flex items-center px-8 mt-4">
            <div className="flex items-center space-x-4">
              <button
                className="primary-button bg-brand-quaternary mb-2 h-11 pt-3"
                onClick={handleSendEmail}>
                Send Email
              </button>
              <Tooltip title="Download billing invoice as a pdf">
                <button onClick={generateBillingPdf}>
                  <DownloadIcon
                    fontSize="large"
                    className="fill-brand-quaternary"
                  />
                </button>
              </Tooltip>

              <button
                className="primary-button bg-brand-primary mb-2 h-11 pt-3"
                onClick={() => setOpenDialog(false)}>
                Cancel
              </button>
            </div>

            <div className="ml-auto flex items-center space-x-4">
              <input
                type="date"
                value={billingDate.toISOString().split("T")[0]} // Format the date as YYYY-MM-DD
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const parsedDate = new Date(inputValue);

                  if (!isNaN(parsedDate.getTime())) {
                    setBillingDate(parsedDate);
                  }
                }}
                className="w-fit border border-black rounded px-2 py-1"
              />
              <button
                className="primary-button bg-brand-secondary mb-2 h-11 pt-3"
                onClick={handleCaptureBillingDate}>
                Save Billed Date
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}

CompanyBillingEmail.propTypes = {
  company: PropTypes.shape({
    billingAddress: PropTypes.string,
    billingDate: PropTypes.string,
    companyAddress: PropTypes.string,
    description: PropTypes.string,
    employerId: PropTypes.number,
    endDate: PropTypes.string,
    membership: PropTypes.shape({
      cost: PropTypes.number,
      membershipType: PropTypes.string,
    }),
    primaryContact: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    }),
    name: PropTypes.string,
    paymentDate: PropTypes.string,
    startDate: PropTypes.string,
    status: PropTypes.string,
    username: PropTypes.string,
  }).isRequired,
  updateTable: PropTypes.func.isRequired,
  setUpdateTable: PropTypes.func.isRequired,
  setOpenSnackbar: PropTypes.func.isRequired,
  setSnackbarMessage: PropTypes.func.isRequired,
};

export default CompanyBillingEmail;
